import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Track } from '@app/core/models/Track';
import { LogModal } from '@app/core/states/events-tracking.actions';
import { RadioState } from '@app/core/states/radio.state';
import { aLogEvent, AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { SavedToLocalstorage } from '@app/shared/constants';
import { weekDaysValidator } from '@app/shared/custom-validators';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { Weekdays, WeekdaySelectValue } from '@radioking/ui/weekday-select';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, startWith, take } from 'rxjs/operators';

import { MatStepper } from '@angular/material/stepper';
import { AppState } from '@app/core/states/app.state';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import { SchedulerTrack } from '../../../../../scheduler-core/src/lib/models/scheduler-core.model';
import { SchedulerCoreState } from '../../../../../scheduler-core/src/lib/states/scheduler-core.state';
import {
  BreakDetails,
  BreakDetailsAndOcc,
  BreakKind,
  BreakPlayType,
} from '../../models/break.model';
import {
  CreateBreakFailure,
  CreateBreakRequest,
  CreateBreakSuccess,
  EditBreakFailure,
  EditBreakRequest,
  EditBreakSuccess,
  NoContentSelected,
} from '../../states/break.actions';
import { BreakState } from '../../states/break.state';

export interface BreakEditionFormValues {
  track?: SchedulerTrack;
  program?: SugestionInput;
  playlist?: SugestionInput;
  startDay: { startDate: moment.Moment; endDate: moment.Moment };
  startHour: string;
  tracksCount: number;
  playType: BreakPlayType;
  priority: number;
  fixedTime: boolean;
  tolerance: number;
  repeat: boolean;
  weekdays: WeekdaySelectValue;
  repeatEndDay?: { startDate: moment.Moment; endDate: moment.Moment };
  dailyRepeat: boolean;
  dailyRepeatInterval: number;
  repeatEndHour: string;
}

/**
 * Form default values
 */
export const formDefaultValues: BreakEditionFormValues = {
  track: null,
  program: null,
  playlist: null,
  startDay: null,
  startHour: null,
  tracksCount: 1,
  playType: 'sequential',
  priority: 5,
  fixedTime: false,
  tolerance: 5,
  repeat: false,
  weekdays: null,
  dailyRepeat: false,
  dailyRepeatInterval: 60,
  repeatEndHour: '23:59',
};

/**
 * BreakEditionComponent
 *
 * Modal dialog component for creating and editing broadcast breaks. Supports three types of content:
 * tracks, programs, and playlists. Handles scheduling, repetition patterns, and priority settings.
 * Provides responsive design with different interfaces for desktop and mobile devices.
 *
 * @input data - Break details and occurrence data for editing mode
 * @output dialogRef.close() - Closes the modal after successful creation/editing
 */
@Component({
  selector: 'rk-break-edition',
  templateUrl: './break-edition.component.html',
  styleUrls: ['./break-edition.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreakEditionComponent implements OnInit, OnDestroy {
  /**
   * Whether the dialog is in edition mode (true) or creation mode (false)
   */
  isEdition = false;

  /**
   * Active tab (defaults to "track" tab)
   *
   * See {@link initializeTabs}
   * See {@link changeActiveTab}
   */
  activeTab: BreakKind;

  /**
   * Form instance
   */
  form: FormGroup;

  /**
   * Radio station timezone for date/time calculations
   */
  radioTz: string;

  /**
   * Minimum allowed date for repeat end date selection
   */
  repeatMinDate: moment.Moment = null;

  /**
   * Maximum allowed date for repeat end date selection (6 months from start)
   */
  repeatMaxDate: moment.Moment = null;

  /**
   * Enums exposed to template
   */
  enums = { BreakKind };

  /**
   * Whether program choices for dropdown are empty or not
   */
  @Select(ProgramsState.hasNotEmptyPrograms)
  hasPrograms$: Observable<boolean>;

  /**
   * Program choices for dropdown
   */
  @Select(ProgramsState.allNotEmptyProgramsForDropdown)
  allPrograms$: Observable<SugestionInput[]>;

  /**
   * Whether playlist choices for dropdown are empty or not
   */
  @Select(SchedulerCoreState.hasNotEmptyPlaylists)
  hasPlaylists$: Observable<boolean>;

  /**
   * Playlist choices for dropdown
   */
  @Select(SchedulerCoreState.allNotEmptyPlaylistsForDropdown)
  allPlaylists$: Observable<SugestionInput[]>;

  /**
   * All available tracks for selection
   */
  @Select(SchedulerCoreState.allTracks)
  allTracks$: Observable<SchedulerTrack[]>;

  /**
   * Formatted event/break date for its form field
   *
   * See {@link formatBreakDate}
   */
  formattedBreakDate = '';

  /**
   * Formatted repeat end date for its form field
   *
   * See {@link formatRepeatDate}
   */
  formattedRepeatDate = '';

  /**
   * Whether tolerance field should be displayed (depends on fixedTime setting)
   */
  isToleranceVisible = false;

  /**
   * Whether repeat options are currently active
   */
  isRepeatActive = false;

  /**
   * Whether daily repeat interval field should be displayed
   */
  isDailyRepeatVisible = false;

  /**
   * Whether dialog is in a loading state or not (when saving)
   */
  isLoading$: Observable<boolean>;

  subscription = new Subscription();

  /**
   * Local storage key for planning hint dismissal
   */
  planningHintKey = SavedToLocalstorage.PLANNING_6_MONTH_HINT;

  /**
   * Gets the break object from dialog data
   */
  get breakObj(): BreakDetails {
    return this.data.break;
  }

  /**
   * Determines if the interface should use mobile layout
   */
  get mobile(): boolean {
    return this.data.mobile;
  }

  /* Mobile-specific variables for stepper navigation and content filtering */
  /**
   * Current step in mobile stepper (0-based)
   */
  $step = signal(0);

  /**
   * Total number of steps in mobile stepper
   */
  $totalSteps = signal(3);

  /**
   * Material stepper component reference for mobile navigation
   */
  @ViewChild('stepper')
  stepper: MatStepper;

  /**
   * Search input for filtering content lists
   */
  searchSubject = new BehaviorSubject<string>('');

  /**
   * Filtered programs list based on search criteria
   */
  $filteredPrograms: WritableSignal<SugestionInput[]> = signal([]);

  /**
   * Filtered playlists list based on search criteria
   */
  $filteredPlaylists: WritableSignal<SugestionInput[]> = signal([]);

  /**
   * Filtered tracks list based on search criteria
   */
  $filteredTracks: WritableSignal<SchedulerTrack[]> = signal([]);

  /**
   * Whether to show all programs in mobile view (expanded list)
   */
  $seeAllPrograms = signal(false);

  /**
   * Whether to show all playlists in mobile view (expanded list)
   */
  $seeAllPlaylists = signal(false);

  /**
   * Whether to show all tracks in mobile view (expanded list)
   */
  $seeAllTracks = signal(false);

  constructor(
    private readonly actions$: Actions,
    private readonly uiStore: UiStoreHelperService,
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<BreakEditionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BreakDetailsAndOcc,
  ) {
    this.radioTz = this.store.selectSnapshot(RadioState.currentRadioTimezone);
    this.activeTab = this.store.selectSnapshot(SchedulerCoreState.allTracks).length
      ? BreakKind.track
      : this.store.selectSnapshot(SchedulerCoreState.hasPlaylists)
        ? BreakKind.playlist
        : BreakKind.program;
  }

  ngOnInit(): void {
    this.initializeEditionMode();
    this.initializeTabs();
    this.initializeForm();

    this.isLoading$ = this.uiStore.getLoadingState(
      [CreateBreakRequest, EditBreakRequest],
      [CreateBreakFailure, CreateBreakSuccess, EditBreakFailure, EditBreakSuccess],
    );

    // Creation/edition succeed: close this dialog
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(CreateBreakSuccess, EditBreakSuccess))
        .subscribe(() => {
          this.closeSelf();
        }),
    );
    this.logModal();

    if (this.mobile) {
      this.subscription.add(
        combineLatest([
          this.allPrograms$,
          this.allPlaylists$,
          this.allTracks$,
          this.searchSubject.asObservable(),
        ]).subscribe(([allPrograms, playlists, tracks, search]) => {
          this.$filteredPrograms.set(
            search.length < 1
              ? allPrograms
              : allPrograms.filter(program =>
                  program.name.toLowerCase().includes(search.toLowerCase()),
                ),
          );
          this.$filteredPlaylists.set(
            search.length < 1
              ? playlists
              : playlists.filter(playlist =>
                  playlist.name.toLowerCase().includes(search.toLowerCase()),
                ),
          );
          this.$filteredTracks.set(
            search.length < 1
              ? tracks
              : tracks.filter(
                  track =>
                    track.title.toLowerCase().includes(search.toLowerCase()) ||
                    track.artist.toLowerCase().includes(search.toLowerCase()),
                ),
          );
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Determine whether we're in edition or creation mode
   */
  initializeEditionMode(): void {
    this.isEdition = Boolean(this.breakObj?.id);
  }

  /**
   * Determine initial active tab
   */
  initializeTabs(): void {
    this.activeTab = this.breakObj?.kind ?? this.activeTab;
    aLogEvent(
      this.activeTab === 'playlist'
        ? AMPLITUDE_EVENT.BREAKS.PLAYLIST
        : this.activeTab === 'program'
          ? AMPLITUDE_EVENT.BREAKS.PROGRAM
          : AMPLITUDE_EVENT.BREAKS.TRACK,
    );
  }

  /**
   * Initialize form (values, events)
   */
  initializeForm(): void {
    const formValues = this.breakToFormValues();
    this.form = this.fb.group({
      track: formValues.track,
      program: formValues.program,
      playlist: formValues.playlist,
      startDay: formValues.startDay,
      startHour: formValues.startHour,
      tracksCount: formValues.tracksCount,
      playType: formValues.playType,
      priority: formValues.priority,
      fixedTime: formValues.fixedTime,
      tolerance: formValues.tolerance,
      repeat: [formValues.repeat, weekDaysValidator()],
      weekdays: [formValues.weekdays, weekDaysValidator(false)],
      repeatEndDay: formValues.repeatEndDay,
      dailyRepeat: formValues.dailyRepeat,
      dailyRepeatInterval: [
        formValues.dailyRepeatInterval,
        [Validators.min(1), Validators.max(1439)], // 1440 minutes in a day
      ],
      repeatEndHour: formValues.repeatEndHour,
    });

    // Default value for "program" field
    this.subscription.add(
      this.allPrograms$
        .pipe(
          filter(v => !!v && this.form.get('program').value === null && v.length > 0),
          filter(() => !this.mobile || (this.mobile && !this.isEdition)),
          take(1),
        )
        .subscribe(programs => {
          this.form.get('program').setValue(programs[0]);
          if (this.mobile) {
            this.activeTab = BreakKind.program;
          }
        }),
    );

    if (!this.mobile || !this.form.get('program').value) {
      // Default value for "playlist" field
      this.subscription.add(
        this.allPlaylists$
          .pipe(
            filter(v => !!v && this.form.get('playlist').value === null && v.length > 0),
            filter(() => !this.mobile || (this.mobile && !this.isEdition)),
            take(1),
          )
          .subscribe(playlists => {
            const playlistField = this.form.get('playlist');

            playlistField.setValue(playlists[0]);
          }),
      );
    }

    this.subscription.add(
      this.form
        .get('playType')
        .valueChanges.pipe(
          startWith(formValues.playType),
          filter(() => this.activeTab === 'playlist'),
        )
        .subscribe(playType => {
          aLogEvent(
            playType === 'sequential'
              ? AMPLITUDE_EVENT.BREAKS.PLAYLIST_SEQUENTIAL
              : AMPLITUDE_EVENT.BREAKS.PLAYLIST_RANDOM,
          );
        }),
    );

    this.subscription.add(
      this.form
        .get('fixedTime')
        .valueChanges.pipe(startWith(formValues.fixedTime))
        .subscribe(fixedTime => this.toggleToleranceField(fixedTime)),
    );

    this.subscription.add(
      this.form
        .get('repeat')
        .valueChanges.pipe(startWith(formValues.repeat))
        .subscribe(repeat => this.toggleRepeat(repeat)),
    );

    this.subscription.add(
      this.form
        .get('dailyRepeat')
        .valueChanges.pipe(startWith(formValues.dailyRepeat))
        .subscribe(repeat => this.toggleDailyRepeat(repeat)),
    );
  }

  /**
   * Convert an event/break to form values
   */
  breakToFormValues(): BreakEditionFormValues {
    const programs = this.store.selectSnapshot(ProgramsState.allProgramsForDropdown);
    const playlists = this.store.selectSnapshot(
      SchedulerCoreState.allPlaylistsForDropdown,
    );
    const track = this.breakObj?.trackId
      ? this.store
          .selectSnapshot(SchedulerCoreState.allTracks)
          .find(t => t.id === this.breakObj?.trackId)
      : formDefaultValues.track;
    const program = this.breakObj?.programId
      ? programs.find(v => v.id === this.breakObj.programId)
      : formDefaultValues.program;
    const playlist = this.breakObj?.playlistId
      ? playlists.find(v => v.id === this.breakObj.playlistId)
      : formDefaultValues.playlist;

    if (this.isEdition && playlist) {
      this.$totalSteps.set(4);
    }

    // Determine initial start day based on device type and allowed date range.
    // Uses BreakState.currentDay for mobile, otherwise ensures start day is within allowed range.
    const createStartDay = this.store.selectSnapshot(AppState.onMobile)
      ? this.store.selectSnapshot(BreakState.currentDay)
      : moment().isBefore(this.store.selectSnapshot(BreakState.datesRange).from)
        ? this.store.selectSnapshot(BreakState.datesRange).from
        : moment();
    // Date is always the first occurrence
    const startDayM = this.isEdition
      ? this.breakObj?.startDay
      : createStartDay.tz(this.radioTz);

    const startDay = {
      startDate: startDayM.clone(),
      endDate: startDayM.clone(), // Same as startDate because we don't want a range
    };

    this.formatBreakDate(startDayM);

    this.repeatMinDate = startDay.startDate.clone();
    this.repeatMaxDate = startDay.startDate.clone().add(6, 'month');
    const startHour = this.breakObj?.startHour ?? startDay.startDate.format('HH:mm');

    const tracksCount = this.breakObj?.tracksCount ?? formDefaultValues.tracksCount;
    const playType = this.breakObj?.playType ?? formDefaultValues.playType;
    const priority = this.breakObj?.priority ?? formDefaultValues.priority;
    const fixedTime = this.breakObj?.fixedTime ?? formDefaultValues.fixedTime;
    const tolerance = this.breakObj?.tolerance ?? formDefaultValues.tolerance;

    // In previous version, no day selected = all days
    const emptyAllDays = !!(
      this.breakObj?.repeat &&
      this.breakObj.repeat?.repeatDays?.length < 1 &&
      this.breakObj.repeat?.repeatEndDay &&
      !this.breakObj.repeat?.repeatEndDay.isSame(this.breakObj.startDay, 'day')
    );
    const repeat =
      this.breakObj?.repeat?.repeatDays || emptyAllDays ? true : formDefaultValues.repeat;
    let weekdays: WeekdaySelectValue = this.breakObj?.repeat?.repeatDays
      ? new Set<Weekdays>(
          this.breakObj.repeat.repeatDays.toString().split(',') as Weekdays[],
        )
      : formDefaultValues.weekdays;
    if (repeat) {
      if (this.breakObj?.repeat?.repeatDays) {
        weekdays = new Set<Weekdays>(
          this.breakObj?.repeat.repeatDays.toString().split(',') as Weekdays[],
        );
      } else if (emptyAllDays) {
        weekdays = new Set<Weekdays>(['1', '2', '3', '4', '5', '6', '7'] as Weekdays[]);
      }
    }

    let repeatEndDate = moment().add(7, 'days');
    if (repeat) {
      if (this.breakObj?.repeat.repeatEndDay) {
        repeatEndDate = this.breakObj?.repeat.repeatEndDay;
      }
    }
    const repeatEndDay = {
      startDate: repeatEndDate.clone(),
      endDate: repeatEndDate.clone(), // Same as startDate because we don't want a range
    };
    this.formatRepeatDate(repeatEndDate);

    const dailyRepeat = this.breakObj?.dailyRepeat ?? formDefaultValues.dailyRepeat;
    const dailyRepeatInterval =
      this.breakObj?.repeatPerDay.repeatInterval ?? formDefaultValues.dailyRepeatInterval;

    const repeatEndHour = this.breakObj?.repeatPerDay?.repeatEndHour
      ? this.breakObj.repeatPerDay?.repeatEndHour.substring(0, 5)
      : formDefaultValues.repeatEndHour;

    return {
      track,
      program,
      playlist,
      startDay,
      startHour,
      tracksCount,
      playType,
      priority,
      fixedTime,
      tolerance,
      repeat,
      repeatEndDay,
      weekdays,
      dailyRepeat,
      dailyRepeatInterval,
      repeatEndHour,
    };
  }

  /**
   * Change active tab to specified one
   */
  changeActiveTab(e: MatButtonToggleChange): void {
    this.activeTab = e.value;
    aLogEvent(
      this.activeTab === 'playlist'
        ? AMPLITUDE_EVENT.BREAKS.PLAYLIST
        : this.activeTab === 'program'
          ? AMPLITUDE_EVENT.BREAKS.PROGRAM
          : AMPLITUDE_EVENT.BREAKS.TRACK,
    );
  }

  /**
   * Event/break's track changed
   */
  breakTrackChanged(track: Track | SchedulerTrack | null): void {
    this.form.get('track').setValue(track);
  }

  /**
   * Break's date changed
   */
  breakDateChanged(event: any): void {
    this.repeatMinDate = event.startDate.clone();
    if (this.form.get('repeatEndDay').value.startDate.isBefore(this.repeatMinDate)) {
      this.form.get('repeatEndDay').setValue({
        startDate: this.repeatMinDate.clone(),
        endDate: this.repeatMinDate.clone(),
      });
      this.formatRepeatDate(this.repeatMinDate);
    }

    this.repeatMaxDate = event.startDate.clone().add(6, 'month');
    if (this.form.get('repeatEndDay').value.startDate.isAfter(this.repeatMaxDate)) {
      this.form.get('repeatEndDay').setValue({
        startDate: this.repeatMaxDate.clone(),
        endDate: this.repeatMaxDate.clone(),
      });
      this.formatRepeatDate(this.repeatMaxDate);
    }
    this.formatBreakDate(event.startDate);
  }

  /**
   * Toggles the visibility of tolerance field based on fixed time setting
   */
  toggleToleranceField(fixedTime: boolean): void {
    this.isToleranceVisible = fixedTime;

    if (!this.isToleranceVisible) {
      // Prevent a hidden field to invalidate form
      this.form.get('tolerance').setValue(formDefaultValues.tolerance);
    } else {
      aLogEvent(AMPLITUDE_EVENT.BREAKS.INTERRUPT_MUSIC);
    }
  }

  /**
   * Toggles repeat functionality and sets default weekday selection
   */
  toggleRepeat(repeat: boolean): void {
    this.isRepeatActive = repeat;
    if (repeat) {
      aLogEvent(AMPLITUDE_EVENT.BREAKS.REPETITION);
    }
    // API wants days from 1 to 7 (sunday), moment give 0 (sunday) to 6
    const weekdayDayOfWeek =
      this.form.get('startDay').value.startDate.day() > 0
        ? this.form.get('startDay').value.startDate.day()
        : 7;
    if (this.form.get('weekdays').pristine && !this.isEdition) {
      this.form.get('weekdays').setValue(new Set([weekdayDayOfWeek.toString()]));
    }
  }

  /**
   * Handles repeat end date changes and logs analytics event
   */
  repeatDateChanged(event: any): void {
    aLogEvent(AMPLITUDE_EVENT.BREAKS.ENDS_DATE);
    this.formatRepeatDate(event.startDate);
  }

  /**
   * Toggles daily repeat functionality
   */
  toggleDailyRepeat(repeat: boolean): void {
    this.isDailyRepeatVisible = repeat;
    if (repeat) {
      aLogEvent(AMPLITUDE_EVENT.BREAKS.REPEAT_DURING_DAY);
    }
  }

  /**
   * "Save" button clicked
   */
  saveClicked(): void {
    const breakObj = this.formValuesToBreak();

    if (this.isEdition) {
      this.store.dispatch(new EditBreakRequest(breakObj));
    } else {
      this.store.dispatch(new CreateBreakRequest(breakObj));
    }
  }

  /**
   * Convert form values to a break
   */
  formValuesToBreak(): BreakDetails {
    const radioTz = this.store.selectSnapshot(RadioState.currentRadioTimezone);
    const formValues: BreakEditionFormValues = this.form.getRawValue();
    const startDay = formValues.startDay.startDate.tz(radioTz, true);
    const startHour = `${formValues.startHour}:00`.substring(0, 8);
    // If start hour is not edited, it already has seconds
    const breakObj: BreakDetails = {
      kind: this.activeTab,
      startDay,
      startHour,
      repeat:
        formValues.repeat && formValues.weekdays
          ? {
              repeatEndDay: formValues.repeatEndDay
                ? moment(formValues.repeatEndDay.startDate)
                : null,
              repeatDays: formValues.weekdays
                ? Array.from(formValues.weekdays).join(',')
                : null,
            }
          : null,
      priority: formValues.priority,
      fixedTime: formValues.fixedTime,
      tolerance: formValues.tolerance,
      dailyRepeat: formValues.dailyRepeat,
      tracksCount: formValues.tracksCount,
      repeatPerDay: {
        repeatEndHour: `${formValues.repeatEndHour}:59`.substring(0, 8),
        // Same as start hour
        repeatInterval: formValues.dailyRepeatInterval,
      },
      playType: formValues.playType,
    };

    if (this.isEdition) {
      breakObj.id = this.breakObj.id;
    }

    if (this.activeTab === BreakKind.track) {
      breakObj.trackId = formValues.track.id;
    } else if (this.activeTab === BreakKind.playlist) {
      breakObj.playlistId = formValues.playlist.id;
    } else if (this.activeTab === BreakKind.program) {
      breakObj.programId = formValues.program.id;
    }

    return breakObj;
  }

  /**
   * Close self (closing dialog)
   */
  closeSelf(): void {
    this.dialogRef.close();
  }

  /**
   * Format break date
   */
  formatBreakDate(breakDate: moment.Moment): void {
    const desiredFormat = moment.localeData().longDateFormat('ll');
    this.formattedBreakDate = breakDate.format(desiredFormat);
  }

  /**
   * Format repeat end date
   */
  formatRepeatDate(date: moment.Moment): void {
    const desiredFormat = moment.localeData().longDateFormat('ll');
    this.formattedRepeatDate = date.format(desiredFormat);
  }

  /**
   * Determines whether to show the 6-month planning hint
   */
  showHint() {
    return !localStorage.getItem(this.planningHintKey);
  }

  /**
   * Dismisses the 6-month planning hint
   */
  dismiss6MonthHint() {
    localStorage.setItem(this.planningHintKey, '1');
  }

  /**
   * Validates and constrains number input values to their min/max bounds
   */
  limitInputNumber(e: Event) {
    const targetEl = e.target as HTMLInputElement;
    const control = this.form.get(targetEl.getAttribute('formcontrolname'));
    if (
      targetEl.max &&
      Number.parseInt(targetEl.max) < Number.parseInt(targetEl.value, 10)
    ) {
      control.setValue(targetEl.max, { onlySelf: true });
      control.markAsPristine();
    }
    if (
      targetEl.min &&
      Number.parseInt(targetEl.min) > Number.parseInt(targetEl.value, 10)
    ) {
      control.setValue(targetEl.min, { onlySelf: true });
    }
    control.updateValueAndValidity();
  }

  /**
   * Logs analytics event when tolerance value is clicked
   */
  clickToleranceValue() {
    aLogEvent(AMPLITUDE_EVENT.BREAKS.INTERRUPT_MUSIC_TIME);
  }

  @Dispatch()
  logModal = () =>
    new LogModal({ name: AMPLITUDE_EVENT.MODAL.ADD_EDIT_BREAK, origin: 'Breaks' });

  /* Mobile-specific navigation and content selection methods */

  /**
   * Advances to next step in mobile stepper with validation
   */
  nextStep() {
    if (
      this.$step() === 0 &&
      this.form.get('program').value === null &&
      this.form.get('playlist').value === null &&
      this.form.get('track').value === null
    ) {
      this.noContentSelected();
      return;
    }
    this.$step.update(v => v + 1);
    this.stepper.next();
  }

  /**
   * Goes back to previous step in mobile stepper
   */
  prevStep() {
    this.$step.update(v => v - 1);
    this.stepper.previous();
  }

  /**
   * Toggles expanded view for programs list in mobile
   */
  toggleAllPrograms() {
    this.$seeAllPrograms.update(v => !v);
  }

  /**
   * Toggles expanded view for playlists list in mobile
   */
  toggleAllPlaylists() {
    this.$seeAllPlaylists.update(v => !v);
  }

  /**
   * Toggles expanded view for tracks list in mobile
   */
  toggleAllTracks() {
    this.$seeAllTracks.update(v => !v);
  }

  /**
   * Updates search filter for content lists
   */
  editSearch(e: string) {
    if (typeof e === 'string') {
      this.searchSubject.next(e);
    }
  }

  /**
   * Selects a program and updates form state accordingly
   */
  selectProgram(program: SugestionInput) {
    this.form.get('program').setValue(program);
    this.form.get('playlist').setValue(null);
    this.form.get('track').setValue(null);
    this.activeTab = BreakKind.program;
    this.$totalSteps.set(3);
  }

  /**
   * Selects a playlist and updates form state accordingly
   */
  selectPlaylist(playlist: SugestionInput) {
    this.form.get('playlist').setValue(playlist);
    this.form.get('program').setValue(null);
    this.form.get('track').setValue(null);
    this.activeTab = BreakKind.playlist;
    this.$totalSteps.set(4);
  }

  /**
   * Selects a track and updates form state accordingly
   */
  selectTrack(track: SchedulerTrack) {
    this.form.get('track').setValue(track);
    this.form.get('program').setValue(null);
    this.form.get('playlist').setValue(null);
    this.activeTab = BreakKind.track;
    this.$totalSteps.set(3);
  }

  /**
   * Returns restricted programs list for mobile view with selected item prioritization
   */
  restrictedProgramList() {
    if (this.$seeAllPrograms()) {
      return this.$filteredPrograms();
    }
    if (this.form.get('program').value) {
      const programIndex = this.$filteredPrograms().findIndex(
        p => p.id === this.form.get('program').value.id,
      );
      if (programIndex > 3) {
        return [
          ...this.$filteredPrograms().slice(0, 3),
          this.$filteredPrograms()[programIndex],
        ];
      }
    }
    return this.$filteredPrograms().slice(0, 4);
  }

  /**
   * Returns restricted playlists list for mobile view with selected item prioritization
   */
  restrictedPlaylistList() {
    if (this.$seeAllPlaylists()) {
      return this.$filteredPlaylists();
    }
    if (this.form.get('playlist').value) {
      const playlistIndex = this.$filteredPlaylists().findIndex(
        p => p.id === this.form.get('playlist').value.id,
      );
      if (playlistIndex > 3) {
        return [
          ...this.$filteredPlaylists().slice(0, 3),
          this.$filteredPlaylists()[playlistIndex],
        ];
      }
    }
    return this.$filteredPlaylists().slice(0, 4);
  }

  /**
   * Returns restricted tracks list for mobile view with selected item prioritization
   */
  restrictedTrackList() {
    if (this.$seeAllTracks()) {
      return this.$filteredTracks();
    }
    if (this.form.get('track').value) {
      const trackIndex = this.$filteredTracks().findIndex(
        t => t.id === this.form.get('track').value.id,
      );
      if (trackIndex > 3) {
        return [
          ...this.$filteredTracks().slice(0, 3),
          this.$filteredTracks()[trackIndex],
        ];
      }
    }
    return this.$filteredTracks().slice(0, 4);
  }

  @Dispatch()
  noContentSelected = () => new NoContentSelected();
}
